import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  attrConfig: null,
  attrFilters: null,
  attrLoading: true,
  mapVersion: null,
  mapVersionList: [],
  mapStatus: null,
  isEditableVersion: null,
  mapTags: [],
  allTags: [],
  rowData: null,
  mapColumns: [],
  mapAttributes: {},
  mapID: null,
  conceptSetData: {},
  tableLoading: true,
  isSearchDisabled: false,
  selectedMapId: "",
  updatedRecords: [],
  cloneRecords: [],
  deletedRecords: [],
};

export const mapSlice = createSlice({
  name: "EIPMapValueDefinition.slice",
  reducerPath: "EIPMapValueDefinition",
  initialState,
  reducers: {
    setAttrConfig: (state, action) => {
      state.attrConfig = action.payload;
    },
    setAttrFilters: (state, action) => {
      state.attrFilters = action.payload;
    },
    setAttrLoading: (state, action) => {
      state.attrLoading = action.payload;
    },
    resetFilters: (state, action) => {
      const { attrObj, searchValues, newSearchValues } = action.payload;

      const startIndex = attrObj._id + 1;
      if (attrObj.child && Object.keys(searchValues).length > startIndex) {
        const newConfigs = { ...state.attrConfig };
        const configs = newConfigs.attributes.configs;
        for (let i = startIndex; i < configs.length; i++) {
          configs[i].values = null;
          delete newSearchValues[configs[i].name];
        }
        state.attrFilters = configs.map((obj, index) => ({
          ...obj,
          _id: index,
          disabled: !obj.values,
          values: !obj.values
            ? []
            : obj.values.map((item, index) => ({
                id: index,
                label: item,
                value: item,
              })),
        }));
        state.attrLoading = false;
        state.attrConfig = newConfigs;
      }
    },
    setMapVersion: (state, action) => {
      state.mapVersion = action.payload;
    },
    setMapVersionList: (state, action) => {
      state.mapVersionList = action.payload;
    },
    setMapValueTags: (state, action) => {
      state.mapValueTags = action.payload;
    },
    setMapStatus: (state, action) => {
      state.mapStatus = action.payload;
    },
    setIsEditableVersion: (state, action) => {
      state.isEditableVersion = action.payload;
    },
    setMapTags: (state, action) => {
      state.mapTags = action.payload;
    },
    setAllTags: (state, action) => {
      state.allTags = action.payload;
    },
    setRowData: (state, action) => {
      state.rowData = action.payload;
    },
    setMapColumns: (state, action) => {
      state.mapColumns = action.payload;
    },
    setMapAttributes: (state, action) => {
      state.mapAttributes = action.payload;
    },
    setMapID: (state, action) => {
      state.mapID = action.payload;
    },
    setConceptSetData: (state, action) => {
      state.conceptSetData = action.payload;
    },
    setTableLoading: (state, action) => {
      state.tableLoading = action.payload;
    },
    setIsSearchDisabled: (state, action) => {
      state.isSearchDisabled = action.payload;
    },
    setSelectedMapId: (state, action) => {
      //if the user changed maps, clear these states so it doesn't think there's changes to save
      if (state.selectedMapId && state.selectedMapId !== action.payload) {
        state.updatedRecords = [];
        state.cloneRecords = [];
        state.deletedRecords = [];
      }
      state.selectedMapId = action.payload;
    },
    setUpdatedRecords: (state, action) => {
      state.updatedRecords = action.payload;
    },
    setAddFormData: (state, action) => {
      state.updatedRecords = [...state.updatedRecords, action.payload];
      state.rowData = [...state.rowData, action.payload];
    },
    setMultiEditFormData: (state, action) => {
      const rows = action.payload;
      rows.forEach((response) => {
        if (response.update) {
          const newRow = { ...response, update: true };
          const rowExists = state.rowData.some((row) => row._id === newRow._id);
          state.rowData = rowExists
            ? state.rowData.map((row) =>
                row._id === newRow._id ? { ...row, ...newRow } : row
              )
            : [...state.rowData, newRow];
          const recordExists = state.updatedRecords.some(
            (row) => row._id === newRow._id
          );
          state.updatedRecords = recordExists
            ? state.updatedRecords.map((row) =>
                row._id === newRow._id ? { ...row, ...newRow } : row
              )
            : [...state.updatedRecords, newRow];
        } else {
          state.rowData = state.rowData.filter((obj) => {
            return obj.addRowId !== response.addRowId;
          });
          state.updatedRecords = state.updatedRecords.filter((obj) => {
            return obj.addRowId !== response.addRowId;
          });
        }
      });
    },
    resetFormData: () => {},
    setMultiDeleteFormData: (state, action) => {
      const rows = action.payload;

      rows.records.forEach((response) => {
        if (response._id) {
          const newRow = { ...response, delete: true };
          const rowExists = state.rowData.some((row) => row._id === newRow._id);
          state.rowData = rowExists
            ? state.rowData.map((row) =>
                row._id === newRow._id ? { ...row, ...newRow } : row
              )
            : [...state.rowData, newRow];
          const recordExists = state.updatedRecords.some(
            (row) => row._id === newRow._id
          );
          state.updatedRecords = recordExists
            ? state.updatedRecords.map((row) =>
                row._id === newRow._id ? { ...row, ...newRow } : row
              )
            : [...state.updatedRecords, newRow];
        } else {
          state.rowData = state.rowData.filter((obj) => {
            return obj.addRowId !== response.addRowId;
          });
          state.deletedRecords = state.deletedRecords.filter((obj) => {
            return obj.addRowId !== response.addRowId;
          });
        }
      });
    },
    setCloneRecords: (state, action) => {
      state.cloneRecords = action.payload;
    },
    setMultiCloneFormData: (state, action) => {
      const rows = action.payload.rows;
      const attributes = action.payload.attributes;
      let cloneId = 1;
      // For new row  _id field would be null. We would use addRowId to differentiate between newly added rows.
      // we are not making use of _id as this should be set by backend api which is an objectID
      var cloneRecords = [];
      var updatedRecords = [];
      rows.forEach((cloneRow) => {
        const row = {
          localCode: cloneRow.localCode,
          standardDescription: cloneRow.standardDescription,
          localDescription: cloneRow.localDescription,
          standardCode: cloneRow.standardCode,
          localSourceSystem: cloneRow.localSourceSystem,
          standardCodeSystem: attributes.Standard_Code_System,
          comments: cloneRow.comments,
          status: cloneRow.status || "NEEDS_REVIEW",
          tags: cloneRow.tags,
          _id: null,
          isNew: true,
          addRowId: cloneId,
        };

        cloneId = cloneId + 1;

        cloneRecords.push(row);
        updatedRecords.push(row);
      });
      state.rowData = [
        ...(Array.isArray(state.rowData) ? state.rowData : []),
        ...cloneRecords,
      ];
      state.updatedRecords = [...state.updatedRecords, ...updatedRecords];
      state.cloneRecords = [...state.cloneRecords, ...cloneRecords];
    },
  },
});

export const {
  resetFormData,
  setAttrConfig,
  setAttrFilters,
  setAttrLoading,
  resetFilters,
  setMapVersion,
  setMapVersionList,
  setMapValueTags,
  setMapStatus,
  setIsEditableVersion,
  setMapAttributes,
  setMapID,
  setConceptSetData,
  setTableLoading,
  setMapTags,
  setAllTags,
  setRowData,
  setMapColumns,
  setIsSearchDisabled,
  setSelectedMapId,
  setAddFormData,
  setUpdatedRecords,
  setMultiEditFormData,
  setMultiDeleteFormData,
  setMultiCloneFormData,
  setCloneRecords,
} = mapSlice.actions;
